/**
 * Export values and file names of the Kennzahlen hub (#2175 B1).
 * Port of mobile-management/src/features/metrics/csv.ts (#1989 G3, C20, C21)
 * and of `formatMetricCsvValue` from its format.ts. The phone shares a
 * semicolon CSV through the share sheet; the web downloads through
 * `ExportButtons` (CSV and XLSX). Both write the same exact cell values (money
 * in francs with two decimals, no grouping), so a spreadsheet sums them to the
 * cent, and both name the file after metric, period and scope.
 *
 * `buildCsv` is kept for parity (csv.test.ts pins it against the phone).
 */

import type { MetricUnit } from "./types";

/** A plain number cell (`-3.25`) is data, never a formula. */
const NUMBER_CELL = /^-?\d+(\.\d+)?$/;

/**
 * One CSV cell: a text cell that starts like a formula (`=`, `+`, `-`, `@`)
 * gets a leading `'` so a spreadsheet shows it as text; quoted when it holds
 * the separator, a quote or a line break.
 */
function cell(raw: string): string {
  const value = /^[=+\-@]/.test(raw) && !NUMBER_CELL.test(raw) ? `'${raw}` : raw;
  return /[";\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Header plus rows, `;`-separated, CRLF line ends, BOM first. */
export function buildCsv(header: readonly string[], rows: readonly (readonly string[])[]): string {
  const lines = [header, ...rows].map((r) => r.map(cell).join(";"));
  return `\uFEFF${lines.join("\r\n")}\r\n`;
}

const GERMAN_LETTERS: Record<string, string> = { "\u00e4": "ae", "\u00f6": "oe", "\u00fc": "ue", "\u00df": "ss" };

/** Lower-case ASCII with dashes: umlauts become "ae"/"oe"/"ue", accents drop. */
export function fileSlug(text: string, max = 60): string {
  return text
    .toLowerCase()
    .replace(/[\u00e4\u00f6\u00fc\u00df]/g, (c) => GERMAN_LETTERS[c])
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, max)
    .replace(/^-+|-+$/g, "");
}

/** `krankheitsquote_2026-01_2026-10_alle-standorte.csv`. */
export function csvFileName(metric: string, from: string, to: string, scope: string): string {
  return `${fileSlug(metric)}_${from}_${to}_${fileSlug(scope)}.csv`;
}

/** The same name without the extension, as `ExportButtons` appends its own. */
export function exportBaseName(metric: string, from: string, to: string, scope: string): string {
  return csvFileName(metric, from, to, scope).replace(/\.csv$/, "");
}

interface Named {
  id: string;
  name?: string | null;
}

export interface CsvScopeLabels {
  /** The whole organisation ("All locations"). */
  all: string;
  /** A selection whose names are not loaded yet. */
  filtered: string;
}

/**
 * The scope part of the file name: the selected groups' names, else the
 * selected locations' names, else the "all" label.
 */
export function csvScopeName(
  locationIds: readonly string[],
  groupIds: readonly string[],
  locations: readonly Named[],
  groups: readonly Named[],
  labels: CsvScopeLabels,
): string {
  const names = (ids: readonly string[], list: readonly Named[]) =>
    ids.map((id) => list.find((x) => x.id === id)?.name).filter((n): n is string => !!n);
  const ids = groupIds.length > 0 ? groupIds : locationIds;
  if (ids.length === 0) return labels.all;
  const found = names(ids, groupIds.length > 0 ? groups : locations);
  return found.length === ids.length ? found.join(" ") : labels.filtered;
}

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  const scaled = Math.abs(value) * factor;
  // Nudge by a relative epsilon so 1.005 rounds like a person rounds it.
  const rounded = Math.round(scaled * (1 + Number.EPSILON)) / factor;
  return value < 0 ? -rounded : rounded;
}

/** A plain decimal for a machine-readable cell (no grouping, a decimal point). */
function csvNumber(value: number, decimals: number): string {
  if (!Number.isFinite(value)) return "";
  const text = roundTo(value, decimals).toFixed(decimals);
  return text === `-${(0).toFixed(decimals)}` ? (0).toFixed(decimals) : text;
}

/** The export cell of a value: a plain decimal (francs for money), no unit. */
export function formatMetricCsvValue(unit: MetricUnit, value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "";
  if (unit === "chf") return csvNumber(value / 100, 2);
  if (unit === "count") return csvNumber(value, Number.isInteger(value) ? 0 : 2);
  return csvNumber(value, 2);
}
